import axios from 'axios';
import { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../configuration/Configuration';
import {
  ListItemButton,
  List,
  Tooltip,
  IconButton,
  Box,
  Autocomplete,
  Button,
  TextField,
  Typography,
  Modal,
  InputAdornment,
} from '@mui/material';
import { Delete, Edit, Save } from '@mui/icons-material';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3,
  borderRadius: 1,

  display: 'flex',
  flexDirection: 'column',
  gap: 3,
};

const normalizeLaneNumericInput = (value) => {
  if (value === undefined || value === null) {
    return '';
  }

  const normalizedValue = String(value)
    .replace(/\./g, ',')
    .replace(/[^0-9,]/g, '');
  const firstCommaIndex = normalizedValue.indexOf(',');

  if (firstCommaIndex === -1) {
    return normalizedValue;
  }

  return `${normalizedValue.slice(0, firstCommaIndex + 1)}${normalizedValue
    .slice(firstCommaIndex + 1)
    .replace(/,/g, '')}`;
};

const formatLaneNumericDisplay = (value) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  return normalizeLaneNumericInput(value);
};

const Department = ({ dep, userType }) => {
  const [departmentInfo, setDepartmentInfo] = useState();
  const [creatingMember, setCreatingMember] = useState(false);
  const [creatingLane, setCreatingLane] = useState(false);
  const [userName, setUserName] = useState();
  const [memberEmailInput, setMemberEmailInput] = useState('');
  const [options, setOptions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [lanes, setLanes] = useState();
  const [km, setKm] = useState();
  const [margin, setMargin] = useState();
  const [price, setPrice] = useState();
  const [memberEmails, setMemberEmails] = useState({});
  const [memberEmailErrors, setMemberEmailErrors] = useState({});
  const [memberEmailSaving, setMemberEmailSaving] = useState({});
  const canAddMembers = Boolean(userType?.isAdmin);
  const decimalInputProps = { inputMode: 'decimal', pattern: '[0-9,]*' };

  const handleLaneNumericInput = (setter) => (event) => {
    setter(normalizeLaneNumericInput(event.target.value));
  };

  useEffect(() => {
    if (departmentInfo?.users) {
      const userArray = [];
      for (const user of departmentInfo.users) {
        userArray.push({ id: user.id, label: user.username });
      }
      setOptions(userArray);
    }
  }, [departmentInfo]);

  useEffect(() => {
    if (departmentInfo?.members) {
      const initialEmails = {};
      for (const member of departmentInfo.members) {
        const key = member.member_id ?? member.id;
        initialEmails[key] = member.email ?? '';
      }
      setMemberEmails(initialEmails);
      setMemberEmailErrors({});
    }
  }, [departmentInfo?.members]);

  useEffect(() => {
    if (dep.id) {
      axios
        .get(`${API_BASE_URL}/api/department/${dep.id}`)
        .then((res) => {
          setDepartmentInfo(res.data);
          setOptions(res.data.users);
        })
        .catch((err) => {
          console.log(err);
        });

      axios
        .get(`${API_BASE_URL}/api/department/countries/`)
        .then((res) => {
          setCountries(res.data);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [dep]);

  const removeMember = (id) => {
    if (id) {
      axios
        .post(`${API_BASE_URL}/api/department/remove-user/`, {
          user_id: id,
          department_id: dep.id,
        })
        .then((res) => {
          setDepartmentInfo({ ...departmentInfo, members: res.data.members });
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  const removeLane = (id) => {
    if (id) {
      axios
        .post(`${API_BASE_URL}/api/department/remove-lane/`, {
          lane_id: id,
          department_id: dep.id,
        })
        .then((res) => {
          setDepartmentInfo({ ...departmentInfo, lanes: res.data.lanes });
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  const addLanes = (e) => {
    e.preventDefault();

    // Validate all required fields
    if (lanes?.origin && lanes?.destination && km && price && margin && isMarginValueInRange(margin)) {
      if (lanes.id) {
        axios
          .post(`${API_BASE_URL}/api/department/update-lanes/${lanes.id}/`, {
            originCountry: lanes.origin,
            destinationCountry: lanes.destination,
            department_id: dep.id,
            suggested_price: price,
            empty_km: km,
            margin: margin,
          })
          .then((res) => {
            setDepartmentInfo({ ...departmentInfo, lanes: res.data.lanes });
            // Reset form after successful submission
            setCreatingLane(false);
            setLanes();
            setKm('');
            setPrice('');
            setMargin('');
          })
          .catch((err) => {
            console.log(err);
          });
      } else {
        axios
          .post(`${API_BASE_URL}/api/department/lanes/`, {
            originCountry: lanes.origin,
            destinationCountry: lanes.destination,
            department_id: dep.id,
            suggested_price: price,
            empty_km: km,
            margin: margin,
          })
          .then((res) => {
            setDepartmentInfo({ ...departmentInfo, lanes: res.data.lanes });
            // Reset form after successful submission
            setCreatingLane(false);
            setLanes();
            setKm('');
            setPrice('');
            setMargin('');
          })
          .catch((err) => {
            console.log(err);
          });
      }
    } else {
      // Show validation error message
      alert(
        'Please fill in all required fields: Origin, Destination, Empty KM, Suggested Price, and Margin'
      );
    }
  };
  const addMember = (e) => {
    e.preventDefault();

    if (!canAddMembers) {
      alert('Only tender managers can add department members.');
      return;
    }

    // Validate that a user is selected
    if (!userName) {
      alert('Please select a username');
      return;
    }

    const user = options.filter((el) => el.label === userName);
    if (user && user.length > 0) {
      axios
        .post(`${API_BASE_URL}/api/department/add/`, {
          user_id: user[0].id,
          department_id: dep.id,
          email: memberEmailInput?.trim() || null,
        })
        .then((res) => {
          setDepartmentInfo({ ...departmentInfo, members: res.data.members });
          // Reset form after successful submission
          setCreatingMember(false);
          setUserName('');
          setMemberEmailInput('');
        })
        .catch((err) => {
          console.log(err);
          if (err.response?.status === 403) {
            alert('Only tender managers can add department members.');
          }
        });
    } else {
      alert('Please select a valid user from the list');
    }
  };

  const editLane = (lane) => {
    setLanes(lane);
    setPrice(formatLaneNumericDisplay(lane.suggested_price));
    setKm(formatLaneNumericDisplay(lane.empty_km));
    setMargin(formatLaneNumericDisplay(lane.margin));
    setCreatingLane(true);
  };

  const handleMemberEmailChange = (memberId, value) => {
    setMemberEmails((prev) => ({ ...prev, [memberId]: value }));
    if (memberEmailErrors[memberId]) {
      setMemberEmailErrors((prev) => ({ ...prev, [memberId]: '' }));
    }
  };

  const saveMemberEmail = (member) => {
    const memberId = member.member_id ?? member.id;
    const email = memberEmails[memberId] ?? '';
    setMemberEmailSaving((prev) => ({ ...prev, [memberId]: true }));

    axios
      .post(`${API_BASE_URL}/api/department/update-member-email/`, {
        member_id: memberId,
        email: email?.trim() || null,
      })
      .then((res) => {
        setDepartmentInfo(res.data);
      })
      .catch((err) => {
        console.log(err);
        const message =
          err.response?.data?.error ||
          'Unable to update email. Please try again.';
        setMemberEmailErrors((prev) => ({ ...prev, [memberId]: message }));
      })
      .finally(() => {
        setMemberEmailSaving((prev) => ({ ...prev, [memberId]: false }));
      });
  };

  // Helper functions for form validation
  const isMarginValueInRange = (val) => {
    if (!val && val !== 0) return false;
    const num = parseFloat(String(val).replace(',', '.'));
    return !isNaN(num) && num >= 0 && num <= 100;
  };

  const isLaneFormValid = () => {
    return lanes?.origin && lanes?.destination && km && price && margin && isMarginValueInRange(margin);
  };

  const isMemberFormValid = () => {
    return userName && userName.trim() !== '';
  };
  return (
    <div>
      <p style={{ marginTop: -35, whiteSpace: 'nowrap' }}>
        Selected Department: {dep.name}
      </p>
      <div className="depWrapper">
        <div className="depWrapper-inner">
          <p className="title">Members</p>
          <List>
            {canAddMembers && (
              <ListItemButton
                onClick={() => {
                  setCreatingMember(true);
                  setCreatingLane(false);
                }}
              >
                + Add Member
              </ListItemButton>
            )}

            {departmentInfo?.members &&
              departmentInfo.members.map((el) => {
                const memberKey = el.member_id ?? el.id;
                const canEditEmail = userType.isAdmin || userType.isStaff;
                const fallbackEmail = el.user_email || '';
                const memberEmailValue = memberEmails[memberKey] ?? '';
                return (
                  <ListItemButton>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexGrow: 1,
                        overflow: 'visible',
                      }}
                    >
                      <Box sx={{ minWidth: 140 }}>{el.username}</Box>
                      {canEditEmail ? (
                        <TextField
                          size="small"
                          label="Email"
                          value={memberEmailValue}
                          onChange={(e) =>
                            handleMemberEmailChange(memberKey, e.target.value)
                          }
                          error={!!memberEmailErrors[memberKey]}
                          helperText={
                            memberEmailErrors[memberKey] ||
                            (memberEmailValue ? '' : fallbackEmail)
                          }
                          sx={{ flexGrow: 1, maxWidth: 320 }}
                        />
                      ) : (
                        <Box
                          sx={{
                            fontSize: '0.85rem',
                            color: 'text.secondary',
                            flexGrow: 1,
                          }}
                        >
                          {memberEmailValue || fallbackEmail || 'No email set'}
                        </Box>
                      )}
                      {canEditEmail && (
                        <Tooltip title="Save email" placement="top">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => saveMemberEmail(el)}
                              disabled={memberEmailSaving[memberKey]}
                            >
                              <Save fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                    <Tooltip
                      onClick={() => removeMember(el.id)}
                      sx={{ marginLeft: 'auto' }}
                      title="Delete"
                      size="small"
                    >
                      <IconButton>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </ListItemButton>
                );
              })}
          </List>
        </div>
        <div className="depWrapper-inner">
          <p className="title">Lanes</p>
          <List>
            <ListItemButton
              onClick={() => {
                setCreatingLane(true);
                setCreatingMember(false);
                setLanes();
                setKm('');
                setPrice('');
                setMargin('');
              }}
            >
              + Add Lane
            </ListItemButton>
            {departmentInfo?.lanes &&
              departmentInfo.lanes.map((el) => {
                return (
                  <ListItemButton>
                    {el.origin} - {el.destination}{' '}
                    <span
                      style={{
                        marginLeft: '30px',
                        fontSize: '12px',
                        width: '100px',
                      }}
                    >
                      {el?.suggested_price &&
                        `Price: ${formatLaneNumericDisplay(el.suggested_price)}€/km`}
                    </span>
                    <span style={{ marginLeft: '15px', fontSize: '12px' }}>
                      {el?.empty_km &&
                        `Empty: ${formatLaneNumericDisplay(el.empty_km)}km`}
                    </span>
                    <span style={{ marginLeft: '15px', fontSize: '12px' }}>
                      {el?.margin &&
                        `Margin: ${formatLaneNumericDisplay(el.margin)}%`}
                    </span>
                    <Tooltip
                      onClick={() => editLane(el)}
                      sx={{ marginLeft: 'auto' }}
                      title="Edit"
                      size="small"
                    >
                      <IconButton>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      onClick={() => removeLane(el.id)}
                      title="Delete"
                      size="small"
                    >
                      <IconButton>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </ListItemButton>
                );
              })}
          </List>
        </div>
      </div>

      <Modal
        open={creatingLane}
        onClose={() => setCreatingLane(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: -1 }}
          >
            {lanes?.id ? 'Edit lane' : 'Add new department lanes'}
          </Typography>

          <Box
            component="form"
            noValidate
            autoComplete="off"
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <div style={{ display: 'flex', gap: 8 }}>
              <Autocomplete
                required
                id="origin"
                size="small"
                options={countries}
                value={lanes?.origin}
                sx={{ width: '50%' }}
                onInputChange={(event, value) => {
                  setLanes({ ...lanes, origin: value });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="From"
                    error={creatingLane && !lanes?.origin}
                    helperText={
                      creatingLane && !lanes?.origin ? 'Origin is required' : ''
                    }
                  />
                )}
              />
              <Autocomplete
                required
                id="destination"
                size="small"
                options={countries}
                value={lanes?.destination}
                sx={{ width: '50%' }}
                onInputChange={(event, value) => {
                  setLanes({ ...lanes, destination: value });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="To"
                    error={creatingLane && !lanes?.destination}
                    helperText={
                      creatingLane && !lanes?.destination
                        ? 'Destination is required'
                        : ''
                    }
                  />
                )}
              />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <TextField
                required
                id="name"
                size="small"
                type="text"
                inputProps={decimalInputProps}
                value={km}
                onChange={handleLaneNumericInput(setKm)}
                label="Empty KM"
                error={creatingLane && !km}
                helperText={creatingLane && !km ? 'Empty KM is required' : ''}
                sx={{ width: '50%' }}
              />
              <TextField
                required
                id="name"
                size="small"
                inputProps={decimalInputProps}
                value={price}
                type="text"
                onChange={handleLaneNumericInput(setPrice)}
                label="Suggested Price"
                error={creatingLane && !price}
                helperText={
                  creatingLane && !price ? 'Suggested Price is required' : ''
                }
                sx={{ width: '50%' }}
              />
            </div>

            <TextField
              required
              id="margin"
              size="small"
              type="text"
              inputProps={decimalInputProps}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              value={margin}
              onChange={handleLaneNumericInput(setMargin)}
              label="Margin"
              error={creatingLane && (!margin || !isMarginValueInRange(margin))}
              helperText={
                creatingLane && !margin
                  ? 'Margin is required'
                  : creatingLane && !isMarginValueInRange(margin)
                  ? 'Margin must be between 0 and 100.'
                  : ''
              }
            />

            <Button
              type="submit"
              onClick={addLanes}
              variant="contained"
              disabled={!isLaneFormValid()}
              sx={{ mt: 1 }}
            >
              {lanes?.id ? 'Edit' : 'Add'}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={creatingMember && canAddMembers}
        onClose={() => setCreatingMember(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: -1 }}
          >
            Add new department member
          </Typography>

          <Box
            textAlign="left"
            component="form"
            noValidate
            autoComplete="off"
            sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
          >
            <Autocomplete
              required
              id="userName"
              size="small"
              options={options}
              onInputChange={(event, value) => {
                setUserName(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Username"
                  error={creatingMember && !userName}
                  helperText={
                    creatingMember && !userName ? 'Username is required' : ''
                  }
                />
              )}
            />
            <TextField
              id="memberEmail"
              size="small"
              label="Email (optional)"
              value={memberEmailInput}
              onChange={(e) => setMemberEmailInput(e.target.value)}
            />
            <Button
              type="submit"
              onClick={addMember}
              variant="contained"
              disabled={!isMemberFormValid()}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default Department;
